import { useCallback } from "react";

import { useAppDispatch } from "../../../app/store";
import QuestionnaireQuestionDto from "../../../models/Request/InitialConversationQuestionDataItem";
import {
  updateInsertQuestionAnswer,
  removeDependentQuestionAnswers,
} from "../../../slices/requestSlice";

/**
 * Hook for managing question answers and their dependencies in the initial conversation step
 */
export const useQuestionAnswers = () => {
  const dispatch = useAppDispatch();

  /**
   * Updates a question answer and removes answers from dependent questions
   * @param questionId - The ID of the question being answered
   * @param answerId - The selected answer ID
   * @param questionItems - The current set of question items
   */
  const updateAnswer = useCallback(
    (
      questionId: number,
      answerId: number | undefined,
      questionItems: QuestionnaireQuestionDto[]
    ) => {
      dispatch(
        updateInsertQuestionAnswer({
          questionId,
          answerId,
          answerIds: [],
          answerText: undefined,
        })
      );

      dispatch(
        removeDependentQuestionAnswers({
          parentQuestionId: questionId,
          parentAnswerId: answerId,
          questionItems,
        })
      );
    },
    [dispatch]
  );

  /**
   * Updates a question's text answer and removes answers from dependent questions
   * @param questionId - The ID of the question being answered
   * @param answerText - The text answer
   * @param questionItems - The current set of question items
   */
  const updateTextAnswer = useCallback(
    (questionId: number, answerText: string | undefined) => {
      dispatch(
        updateInsertQuestionAnswer({
          questionId,
          answerText,
        })
      );
    },
    [dispatch]
  );

  /**
   * Updates a question's multi-select answers and removes answers from dependent questions
   * @param questionId - The ID of the question being answered
   * @param answerIds - The selected answer IDs
   * @param questionItems - The current set of question items
   */
  const updateMultiAnswer = useCallback(
    (questionId: number, answerIds: number[]) => {
      dispatch(
        updateInsertQuestionAnswer({
          questionId,
          answerIds,
        })
      );
    },
    [dispatch]
  );

  return {
    updateAnswer,
    updateTextAnswer,
    updateMultiAnswer,
  };
};
